// Local translations
export const T = {
  en_GB: {
    process: "Process",
    processDescription: (flowName: string) => `Follow the post-submission process for ${flowName}`,
    notSubmitted:
      "This flow has not been submitted yet. Submit it from the flow page to start the process.",
    backToFlow: "Back to flow",
    reviewSubmission: "Review submission",
    // Submitted step
    submittedTitle: "Flow submitted",
    submittedDescription: "Generate the PDF to continue, or unlock the flow to go back to editing.",
    unlock: "Unlock for editing",
    unlockConfirmTitle: "Unlock flow instance?",
    unlockConfirmDescription:
      "The flow goes back to editing and must be submitted again to restart the process.",
    // QL-292 — back to draft from any later step
    returnToDraft: "Reopen flow",
    // PDF step
    pdfTitle: "PDF generated",
    pdfDescription:
      "Deleting the PDF moves the process back one step. Continue to proceed without regenerating.",
    continue: "Continue",
    // Compliance step
    complianceTitle: "Waiting for compliance approval",
    complianceDescription:
      "A compliance officer must approve this flow before the process can continue.",
    withdrawRequest: "Withdraw request",
    withdrawComplianceConfirmTitle: "Withdraw compliance request?",
    withdrawComplianceConfirmDescription:
      "The request is removed from the compliance queue and the process moves back one step.",
    requestedAt: (date: string) => `Requested ${date}`,
    approve: "Approve",
    reject: "Reject",
    approveConfirmTitle: "Approve flow instance?",
    approveConfirmDescription: "The process moves on to the next step.",
    rejectTitle: "Reject flow instance",
    rejectDescription: "The process moves back one step. A comment is required.",
    rejectCommentLabel: "Rejection comment",
    rejectCommentPlaceholder: "Explain why this flow is rejected…",
    cancel: "Cancel",
    activityLog: "Activity log",
    showAllHistory: (count: number) => `Show all (${count})`,
    showLessHistory: "Show less",
    // External step
    externalTitle: "Waiting for external approval",
    externalDescription:
      "An external system must confirm this flow via the API before the process can continue.",
    withdrawExternalConfirmTitle: "Withdraw external approval request?",
    withdrawExternalConfirmDescription: "The process moves back one step.",
    externalLatestUpdate: "Latest update from the external system",
    // Signing step
    signingBackConfirmTitle: "Move the process back one step?",
    signingBackConfirmDescription:
      "The flow leaves the signing step and can be edited or re-approved before signing is requested again.",
    deleteSignatureConfirmTitle: "Delete signature request?",
    deleteSignatureConfirmDescription:
      "The signing link stops working and the client can no longer sign. You can create a new request afterwards.",
    delete: "Delete",
    // Completed
    completedTitle: "Process completed",
    completedDescription: "All steps are done. This flow instance is final and locked.",
    // Cancelled
    cancelledTitle: "Advice cancelled",
    cancelledDescription:
      "This flow instance was cancelled. You can still generate a PDF to document the decision, or reopen the flow for editing.",
    reopen: "Reopen for editing",
    reopenConfirmTitle: "Reopen flow instance?",
    reopenConfirmDescription: "The flow goes back to draft and can be edited again.",
    error: "Something went wrong",
    processMoved:
      "The process moved to another step while you were viewing this page — the view has been updated.",
    // ── Activity-log event labels ──
    eventLabels: {
      started: "Started",
      submitted: "Submitted",
      unlocked: "Unlocked for editing",
      drafted: "Returned to draft",
      cancelled: "Advice cancelled",
      reopened: "Reopened for editing",
      pdf_generated: "PDF generated",
      pdf_deleted: "PDF deleted",
      // Compliance REVIEW of completed advice — deliberately worded apart from the
      // `compliance_*` entries below, which are the in-process approval gate.
      compliance_review_flagged: "Flagged for compliance review",
      compliance_review_remark: "Compliance review: remark",
      compliance_review_approved: "Compliance review: approved",
      compliance_requested: "Compliance approval requested",
      compliance_approved: "Compliance approved",
      compliance_rejected: "Compliance rejected",
      compliance_withdrawn: "Compliance request withdrawn",
      external_requested: "External approval requested",
      external_update: "External system update",
      external_approved: "External approval granted",
      external_rejected: "External approval rejected",
      external_withdrawn: "External request withdrawn",
      signature_requested: "Signature requested",
      signature_cancelled: "Signature request cancelled",
      signed: "Documents signed",
      completed: "Process completed",
      accounts_orders_created: "Accounts and orders",
      // QL-263 — flow setting "generate PDF automatically on submit"
      auto_pdf_generated: "PDF generated automatically on submit",
      auto_pdf_failed: "Automatic PDF generation failed — generate the PDF manually",
    },
    // QL-292 — "Returned to draft from External approval".
    eventFrom: (label: string, from: string) => `${label} from ${from}`,
  },
  nb_NO: {
    process: "Prosess",
    processDescription: (flowName: string) => `Følg prosessen etter innsending for ${flowName}`,
    notSubmitted:
      "Denne flyten er ikke sendt inn ennå. Send den inn fra flytsiden for å starte prosessen.",
    backToFlow: "Tilbake til flyten",
    reviewSubmission: "Se gjennom innsending",
    submittedTitle: "Flyt sendt inn",
    submittedDescription:
      "Generer PDF-en for å fortsette, eller lås opp flyten for å gå tilbake til redigering.",
    unlock: "Lås opp for redigering",
    unlockConfirmTitle: "Låse opp flytinstansen?",
    unlockConfirmDescription:
      "Flyten går tilbake til redigering og må sendes inn på nytt for å starte prosessen igjen.",
    returnToDraft: "Gjenåpne flyten",
    pdfTitle: "PDF generert",
    pdfDescription:
      "Sletting av PDF-en flytter prosessen ett trinn tilbake. Fortsett for å gå videre uten å regenerere.",
    continue: "Fortsett",
    complianceTitle: "Venter på compliance-godkjenning",
    complianceDescription:
      "En compliance-ansvarlig må godkjenne denne flyten før prosessen kan fortsette.",
    withdrawRequest: "Trekk forespørselen",
    withdrawComplianceConfirmTitle: "Trekke compliance-forespørselen?",
    withdrawComplianceConfirmDescription:
      "Forespørselen fjernes fra compliance-køen og prosessen flyttes ett trinn tilbake.",
    requestedAt: (date: string) => `Forespurt ${date}`,
    approve: "Godkjenn",
    reject: "Avvis",
    approveConfirmTitle: "Godkjenne flytinstansen?",
    approveConfirmDescription: "Prosessen går videre til neste trinn.",
    rejectTitle: "Avvis flytinstans",
    rejectDescription: "Prosessen flyttes ett trinn tilbake. En kommentar er påkrevd.",
    rejectCommentLabel: "Avvisningskommentar",
    rejectCommentPlaceholder: "Forklar hvorfor denne flyten avvises…",
    cancel: "Avbryt",
    activityLog: "Aktivitetslogg",
    showAllHistory: (count: number) => `Vis alle (${count})`,
    showLessHistory: "Vis færre",
    externalTitle: "Venter på ekstern godkjenning",
    externalDescription:
      "Et eksternt system må bekrefte denne flyten via API-et før prosessen kan fortsette.",
    withdrawExternalConfirmTitle: "Trekke forespørselen om ekstern godkjenning?",
    withdrawExternalConfirmDescription: "Prosessen flyttes ett trinn tilbake.",
    externalLatestUpdate: "Siste oppdatering fra det eksterne systemet",
    signingBackConfirmTitle: "Flytte prosessen ett trinn tilbake?",
    signingBackConfirmDescription:
      "Flyten forlater signeringstrinnet og kan redigeres eller godkjennes på nytt før signering forespørres igjen.",
    deleteSignatureConfirmTitle: "Slette signeringsforespørselen?",
    deleteSignatureConfirmDescription:
      "Signeringslenken slutter å virke og kunden kan ikke lenger signere. Du kan opprette en ny forespørsel etterpå.",
    delete: "Slett",
    completedTitle: "Prosess fullført",
    completedDescription: "Alle trinn er fullført. Denne flytinstansen er endelig og låst.",
    cancelledTitle: "Råd kansellert",
    cancelledDescription:
      "Denne flytinstansen ble kansellert. Du kan fortsatt generere en PDF for å dokumentere beslutningen, eller gjenåpne flyten for redigering.",
    reopen: "Gjenåpne for redigering",
    reopenConfirmTitle: "Gjenåpne flytinstansen?",
    reopenConfirmDescription: "Flyten går tilbake til utkast og kan redigeres igjen.",
    error: "Noe gikk galt",
    processMoved:
      "Prosessen gikk videre til et annet trinn mens du så på denne siden — visningen er oppdatert.",
    eventLabels: {
      started: "Startet",
      submitted: "Sendt inn",
      unlocked: "Låst opp for redigering",
      drafted: "Sendt tilbake til utkast",
      cancelled: "Råd kansellert",
      reopened: "Gjenåpnet for redigering",
      pdf_generated: "PDF generert",
      pdf_deleted: "PDF slettet",
      compliance_review_flagged: "Flagget for compliance-gjennomgang",
      compliance_review_remark: "Compliance-gjennomgang: merknad",
      compliance_review_approved: "Compliance-gjennomgang: godkjent",
      compliance_requested: "Compliance-godkjenning forespurt",
      compliance_approved: "Compliance godkjent",
      compliance_rejected: "Compliance avvist",
      compliance_withdrawn: "Compliance-forespørsel trukket",
      external_requested: "Ekstern godkjenning forespurt",
      external_update: "Oppdatering fra eksternt system",
      external_approved: "Ekstern godkjenning gitt",
      external_rejected: "Ekstern godkjenning avvist",
      external_withdrawn: "Ekstern forespørsel trukket",
      signature_requested: "Signatur forespurt",
      signature_cancelled: "Signeringsforespørsel kansellert",
      signed: "Dokumenter signert",
      completed: "Prosess fullført",
      accounts_orders_created: "Kontoer og ordrer",
      auto_pdf_generated: "PDF generert automatisk ved innsending",
      auto_pdf_failed: "Automatisk PDF-generering feilet — generer PDF-en manuelt",
    },
    eventFrom: (label: string, from: string) => `${label} fra ${from}`,
  },
} as const;
